import { signal, useSignal, useSignalEffect } from '@preact/signals'
import { useCallback, useEffect, useMemo, useRef } from 'preact/hooks'
import {
  Color,
  NotificationBuilder,
  type NotificationConfiguration,
  type NotificationPresenter,
} from '@scandit/web-datacapture-core'
import type AppStore from '../store/AppStore'
import type { FlowProps } from './flowTypes'

export interface VinState {
  isPaused: boolean
  userExpanded: boolean
}

export function applyScan(_state: VinState): VinState {
  return { isPaused: true, userExpanded: false }
}

export function applyResume(_state: VinState): VinState {
  return { isPaused: false, userExpanded: false }
}

export function applyExpandChange(state: VinState, isExpanded: boolean): VinState {
  return { ...state, userExpanded: isExpanded }
}

export function shouldFireOnScan(state: VinState): boolean {
  return !state.isPaused
}

export function vinFlowVisibility(state: VinState): { visible: boolean; expanded: boolean } {
  return { visible: state.isPaused, expanded: state.userExpanded }
}

const NOTIFICATION_TAG = 'vin-tap-to-resume'

export interface UseVinPauseFlowOptions {
  dataCaptureViewElementId?: string
  getNotificationPresenter?: () => NotificationPresenter | null
}

function defaultGetNotificationPresenter(store: AppStore): NotificationPresenter | null {
  try {
    return store.sdk?.view?.getNotificationPresenter() ?? null
  } catch (error) {
    console.warn('useVinPauseFlow: NotificationPresenter unavailable', error)
    return null
  }
}

export function useVinPauseFlow(
  store: AppStore,
  notificationText: string,
  options?: UseVinPauseFlowOptions
): FlowProps {
  const isPaused = useSignal(false)
  const userExpanded = useSignal(false)
  const elementId = options?.dataCaptureViewElementId ?? 'data-capture-view'
  const getPresenter = options?.getNotificationPresenter ?? (() => defaultGetNotificationPresenter(store))

  const notificationConfig = useMemo<NotificationConfiguration>(
    () =>
      NotificationBuilder.init(notificationText, NOTIFICATION_TAG)
        .withBackgroundColor(Color.fromHex('FFFFFF'))
        .withTextColor(Color.fromHex('000000'))
        .build(),
    [notificationText]
  )

  const tapListenerRef = useRef<((event: PointerEvent) => void) | null>(null)

  // Stable refs so the unmount cleanup effect never needs to re-register
  const elementIdRef = useRef(elementId)
  elementIdRef.current = elementId
  const getPresenterRef = useRef(getPresenter)
  getPresenterRef.current = getPresenter
  const notificationConfigRef = useRef(notificationConfig)
  notificationConfigRef.current = notificationConfig

  const showNotification = useCallback(async () => {
    const presenter = getPresenterRef.current()
    await presenter?.showNotification(notificationConfigRef.current)
  }, [])

  const hideNotification = useCallback(async () => {
    const presenter = getPresenterRef.current()
    await presenter?.hideNotification(notificationConfigRef.current)
  }, [])

  const detachTapListener = useCallback(() => {
    const listener = tapListenerRef.current
    if (!listener) return
    const element = document.getElementById(elementIdRef.current)
    element?.removeEventListener('pointerdown', listener)
    tapListenerRef.current = null
  }, [])

  const resume = useCallback(async () => {
    const next = applyResume({ isPaused: isPaused.peek(), userExpanded: userExpanded.peek() })
    isPaused.value = next.isPaused
    userExpanded.value = next.userExpanded
    detachTapListener()
    await store.resumeScanning()
    // hideNotification fire-and-forget: the SDK presenter queues show/hide in
    // order on the worker, so we don't risk show-after-hide. Awaiting here would
    // delay resume() with no user-visible benefit.
    void hideNotification()
  }, [store, isPaused, userExpanded, detachTapListener, hideNotification])

  const attachTapListener = useCallback(() => {
    if (tapListenerRef.current) return
    const element = document.getElementById(elementIdRef.current)
    if (!element) {
      console.warn(`useVinPauseFlow: #${elementIdRef.current} not found; tap-to-resume disabled`)
      return
    }
    const listener = () => {
      void resume()
    }
    tapListenerRef.current = listener
    element.addEventListener('pointerdown', listener, { once: true })
  }, [resume])

  const pause = useCallback(async () => {
    // Use peek() to avoid tracking isPaused/userExpanded inside useSignalEffect
    const state = { isPaused: isPaused.peek(), userExpanded: userExpanded.peek() }
    if (!shouldFireOnScan(state)) return
    const next = applyScan(state)
    isPaused.value = next.isPaused
    userExpanded.value = next.userExpanded
    await store.pauseScanning()
    // showNotification fire-and-forget: the SDK presenter serializes show/hide on
    // the worker queue, so a fast tap that triggers hideNotification before show
    // completes still gets handled in order. Awaiting would block tap-listener
    // attachment with no real benefit.
    void showNotification()
    attachTapListener()
  }, [store, isPaused, userExpanded, showNotification, attachTapListener])

  useSignalEffect(() => {
    const qty = store.listStore?.totalQuantity?.value ?? 0
    if (qty > 0) {
      void pause()
    }
  })

  // Run cleanup only on unmount — stable deps ensure this never re-fires mid-lifecycle.
  // hideNotification is async now; fire-and-forget on unmount is intentional (we can't
  // await in a sync cleanup function and the component is going away anyway).
  useEffect(() => {
    return () => {
      detachTapListener()
      void hideNotification()
    }
  }, [])

  const onExpandChange = useCallback(
    (isExpanded: boolean) => {
      const next = applyExpandChange({ isPaused: isPaused.peek(), userExpanded: userExpanded.peek() }, isExpanded)
      isPaused.value = next.isPaused
      userExpanded.value = next.userExpanded
    },
    [isPaused, userExpanded]
  )

  const onClearList = useCallback(async () => {
    store.listStore?.removeAll()
    await resume()
  }, [store, resume])

  const visibility = vinFlowVisibility({ isPaused: isPaused.value, userExpanded: userExpanded.value })

  return {
    totalQuantity: store.listStore?.totalQuantity ?? signal(0),
    items: store.listStore?.resultItems ?? signal([]),
    visible: visibility.visible,
    expanded: visibility.expanded,
    onExpandChange,
    onClearList,
    onResumeScanning: resume,
    showDrawerBottomSheetArrow: true,
  }
}
